import React, { useEffect, useState } from "react";
import { getCreditAnalysisFinancialProfile } from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  // VerticalAlign,
  Packer,
  convertInchesToTwip,
  TableLayoutType,
  PageOrientation,
  Footer,
  ImageRun
  
} from "docx";
import { saveAs } from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { getTenantById } from "../../../../shared/services/apis";

interface FinancialProfileProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

export const FinancialProfile: React.FC<FinancialProfileProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

    useEffect(() => {
      if (!tenantId) return;
  
      const fetchTenant = async () => {
        try {
          const data = await getTenantById(tenantId);
          setTenant(data);
        } catch (error) {
          console.error("Error fetching tenant:", error);
        }
      };
  
      fetchTenant();
    }, [tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCreditAnalysisFinancialProfile(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
        setError(null);
      } catch (err) {
        console.error("Error fetching financial profile:", err);
        setError("Failed to load financial profile");
      } finally {
        setLoading(false);
      }
    };

    if (tenantId && tenantServiceId && tenantServiceOptionId) {
      fetchData();
    }
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

const downloadPDF = async () => {
  setDownloading(true);

  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const financialData = data?.content;

    // Load the watermark image with proper typing
    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    // Load watermark image if logoUrl exists
    let watermarkImg: HTMLImageElement | null = null;
    if (tenant?.logoUrl) {
      try {
        watermarkImg = await loadImage(tenant.logoUrl);
      } catch (error) {
        console.warn('Failed to load watermark image:', error);
      }
    }

    // Function to add watermark to a page
    const addWatermark = (pageNumber: number) => {
      if (!watermarkImg) return;
      
      doc.setPage(pageNumber);
      
      // Save graphics state and set transparency
      doc.saveGraphicsState();
      doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword
      
      // Calculate watermark size and position (centered)
      const watermarkWidth = 80;
      const watermarkHeight = (watermarkImg.height / watermarkImg.width) * watermarkWidth;
      const xPos = (pageWidth - watermarkWidth) / 2;
      const yPos = (pageHeight - watermarkHeight) / 2;
      
      // Add watermark image
      doc.addImage(
        watermarkImg,
        'PNG',
        xPos,
        yPos,
        watermarkWidth,
        watermarkHeight
      );
      
      // Restore graphics state
      doc.restoreGraphicsState();
    };

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Financial Profile", pageWidth / 2, 20, { align: "center" });

    // Subtitle with date and time
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    const currentDateTime = new Date().toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
      align: "center",
    });

    let yPosition = 40;

    // Balance Sheet Section
    if (financialData?.balance_sheet) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Balance Sheet (${financialData.balance_sheet.year})`,
        14,
        yPosition
      );
      yPosition += 8;

      const balanceSheetData = [
        [
          "Total Assets",
          `${financialData.balance_sheet.total_assets?.value || "N/A"} ${
            financialData.balance_sheet.total_assets?.unit || ""
          }`,
        ],
        [
          "Total Equity",
          `${financialData.balance_sheet.total_equity?.value || "N/A"} ${
            financialData.balance_sheet.total_equity?.unit || ""
          }`,
        ],
        [
          "Total Liabilities",
          `${financialData.balance_sheet.total_liabilities?.value || "N/A"} ${
            financialData.balance_sheet.total_liabilities?.unit || ""
          }`,
        ],
        [
          "Cash & Equivalents",
          `${
            financialData.balance_sheet.cash_and_equivalents?.value || "N/A"
          } ${financialData.balance_sheet.cash_and_equivalents?.unit || ""}`,
        ],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [["Metric", "Value"]],
        body: balanceSheetData,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: "bold",
        },
        margin: { left: 14, right: 14 },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    // Income Statement Section
    if (financialData?.income_statement) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(
        `Income Statement (${financialData.income_statement.year})`,
        14,
        yPosition
      );
      yPosition += 8;

      const incomeStatementData = [
        [
          "Revenue",
          `${financialData.income_statement.revenue?.value || "N/A"} ${
            financialData.income_statement.revenue?.unit || ""
          }`,
        ],
        [
          "Gross Profit",
          `${financialData.income_statement.gross_profit?.value || "N/A"} ${
            financialData.income_statement.gross_profit?.unit || ""
          }`,
        ],
        [
          "Operating Income",
          `${
            financialData.income_statement.operating_income?.value || "N/A"
          } ${financialData.income_statement.operating_income?.unit || ""}`,
        ],
        [
          "Net Profit",
          `${financialData.income_statement.net_profit?.value || "N/A"} ${
            financialData.income_statement.net_profit?.unit || ""
          }`,
        ],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [["Metric", "Value"]],
        body: incomeStatementData,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: "bold",
        },
        margin: { left: 14, right: 14 },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    // Key Ratios Section
    if (financialData?.key_ratios && financialData.key_ratios.length > 0) {
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Key Financial Ratios", 14, yPosition);
      yPosition += 8;

      const years =
        financialData.key_ratios[0]?.values?.map((v: any) => v.year) || [];
      const headers = ["Ratio", ...years, "Unit"];

      const ratiosData = financialData.key_ratios.map((ratio: any) => {
        const values = ratio.values.map((v: any) => v.value);
        return [ratio.ratio, ...values, ratio.unit];
      });

      autoTable(doc, {
        startY: yPosition,
        head: [headers],
        body: ratiosData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: "bold",
        },
        margin: { left: 14, right: 14 },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    // Credit Metrics Section
    if (financialData?.credit_metrics) {
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Credit Metrics", 14, yPosition);
      yPosition += 8;

      const creditMetricsData = Object.entries(
        financialData.credit_metrics
      ).map(([key, value]: [string, any]) => [
        key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        `${value?.value || "N/A"} ${value?.unit || ""}`,
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Metric", "Value"]],
        body: creditMetricsData,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
          fillColor: [243, 244, 246],
          textColor: [55, 65, 81],
          fontStyle: "bold",
        },
        margin: { left: 14, right: 14 },
      });
    }

    // Add watermark to all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      addWatermark(i);
    }

    // Footer on every page
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
        align: "center",
      });
    }

    // Save PDF
    const fileName = `Financial_Profile_${new Date().getTime()}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Failed to generate PDF. Please try again.");
  } finally {
    setDownloading(false);
  }
};





const downloadWord = async () => {
  setDownloadingWord(true);

  try {
    // Helper function to convert base64 to Uint8Array
    const base64ToUint8Array = (base64: string): Uint8Array => {
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    };

    // Load logo image if logoUrl exists
    let logoImageData: { data: Uint8Array; mimeType: "jpg" | "png" | "gif" | "bmp" } | null = null;
    if (tenant?.logoUrl) {
      try {
        console.log('Loading logo from:', tenant.logoUrl);
        const response = await fetch(tenant.logoUrl);
        const blob = await response.blob();
        
        // Map MIME type to docx accepted format
        const getMimeType = (blobType: string): "jpg" | "png" | "gif" | "bmp" => {
          if (blobType.includes('png')) return 'png';
          if (blobType.includes('jpeg') || blobType.includes('jpg')) return 'jpg';
          if (blobType.includes('gif')) return 'gif';
          if (blobType.includes('bmp')) return 'bmp';
          return 'png';
        };
        
        const mimeType = getMimeType(blob.type);
        console.log('Detected MIME type:', mimeType);
        
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        const uint8Array = base64ToUint8Array(base64);
        console.log('Logo loaded, size:', uint8Array.length, 'bytes');
        
        logoImageData = { data: uint8Array, mimeType };
      } catch (error) {
        console.error('Failed to load logo image:', error);
      }
    }

    const financialData = data?.content;
    const children: (Paragraph | Table)[] = [];

    // Calculate page width minus margins
    const pageWidth = convertInchesToTwip(8.5);
    const marginLeft = convertInchesToTwip(0.75);
    const marginRight = convertInchesToTwip(0.75);
    const availableWidth = pageWidth - marginLeft - marginRight;

    // Header
    children.push(
      new Paragraph({
        text: "Financial Profile",
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200, before: 200 },
      })
    );

    const currentDateTime = new Date().toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Generated on ${currentDateTime}`,
            size: 20,
            color: "666666",
            italics: true,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 600 },
      })
    );

    // Balance Sheet
    if (financialData?.balance_sheet) {
      children.push(
        new Paragraph({
          text: `Balance Sheet (${financialData.balance_sheet.year})`,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      );

      const col1Width = availableWidth * 0.4;
      const col2Width = availableWidth * 0.6;

      const balanceSheetRows = [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Metric",
                      bold: true,
                      size: 20,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: col1Width, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Value",
                      bold: true,
                      size: 20,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: col2Width, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
          ],
          tableHeader: true,
        }),
      ];

      const balanceItems = [
        { key: "total_assets", label: "Total Assets" },
        { key: "total_equity", label: "Total Equity" },
        { key: "total_liabilities", label: "Total Liabilities" },
        { key: "cash_and_equivalents", label: "Cash & Equivalents" },
      ];

      balanceItems.forEach((item, idx) => {
        const value = (financialData.balance_sheet as any)[item.key];
        balanceSheetRows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    text: item.label,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: col1Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    text: `${value?.value || "N/A"} ${value?.unit || ""}`,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: col2Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
          })
        );
      });

      const balanceSheetTable = new Table({
        rows: balanceSheetRows,
        width: { size: availableWidth, type: WidthType.DXA },
        columnWidths: [col1Width, col2Width],
        layout: TableLayoutType.FIXED,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          insideHorizontal: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
          insideVertical: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
        },
      });

      children.push(balanceSheetTable);
      children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
    }

    // Income Statement
    if (financialData?.income_statement) {
      children.push(
        new Paragraph({
          text: `Income Statement (${financialData.income_statement.year})`,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      );

      const col1Width = availableWidth * 0.4;
      const col2Width = availableWidth * 0.6;

      const incomeStatementRows = [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Metric",
                      bold: true,
                      size: 20,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: col1Width, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Value",
                      bold: true,
                      size: 20,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: col2Width, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
          ],
          tableHeader: true,
        }),
      ];

      const incomeItems = [
        { key: "revenue", label: "Revenue" },
        { key: "gross_profit", label: "Gross Profit" },
        { key: "operating_income", label: "Operating Income" },
        { key: "net_profit", label: "Net Profit" },
      ];

      incomeItems.forEach((item, idx) => {
        const value = (financialData.income_statement as any)[item.key];
        incomeStatementRows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    text: item.label,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: col1Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    text: `${value?.value || "N/A"} ${value?.unit || ""}`,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: col2Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
          })
        );
      });

      const incomeStatementTable = new Table({
        rows: incomeStatementRows,
        width: { size: availableWidth, type: WidthType.DXA },
        columnWidths: [col1Width, col2Width],
        layout: TableLayoutType.FIXED,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          insideHorizontal: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
          insideVertical: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
        },
      });

      children.push(incomeStatementTable);
      children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
    }

    // Key Ratios
    if (financialData?.key_ratios && financialData.key_ratios.length > 0) {
      children.push(
        new Paragraph({
          text: "Key Financial Ratios",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      );

      const years =
        financialData.key_ratios[0]?.values?.map((v: any) => v.year) || [];
      const numCols = years.length + 2;
      const colWidth = availableWidth / numCols;

      const ratioRows = [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Ratio",
                      bold: true,
                      size: 18,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: colWidth, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
            ...years.map(
              (year: string) =>
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: year.toString(),
                          bold: true,
                          size: 18,
                          color: "FFFFFF",
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: "4472C4" },
                  width: { size: colWidth, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                })
            ),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Unit",
                      bold: true,
                      size: 18,
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
              shading: { fill: "4472C4" },
              width: { size: colWidth, type: WidthType.DXA },
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.08),
                right: convertInchesToTwip(0.08),
              },
            }),
          ],
          tableHeader: true,
        }),
      ];

      financialData.key_ratios.forEach((ratio: any, idx: number) => {
        ratioRows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    text: ratio.ratio,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: colWidth, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              ...ratio.values.map(
                (val: any) =>
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: val.value.toString(),
                        spacing: { after: 80, before: 80 },
                      }),
                    ],
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    width: { size: colWidth, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  })
              ),
              new TableCell({
                children: [
                  new Paragraph({
                    text: ratio.unit,
                    spacing: { after: 80, before: 80 },
                  }),
                ],
                shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                width: { size: colWidth, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
          })
        );
      });

      const columnWidths = Array(numCols).fill(colWidth);

      const ratiosTable = new Table({
        rows: ratioRows,
        width: { size: availableWidth, type: WidthType.DXA },
        columnWidths: columnWidths,
        layout: TableLayoutType.FIXED,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
          insideHorizontal: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
          insideVertical: {
            style: BorderStyle.SINGLE,
            size: 1,
            color: "DDDDDD",
          },
        },
      });

      children.push(ratiosTable);
    }

    // Create footer with logo in right bottom corner
    const footerChildren: Paragraph[] = [];
    
    if (logoImageData) {
      footerChildren.push(
        new Paragraph({
          children: [
            new ImageRun({
              type: logoImageData.mimeType,
              data: logoImageData.data,
              transformation: {
                width: 60,
                height: 60,
              },
            }),
          ],
          alignment: AlignmentType.RIGHT,
          spacing: { before: 100 },
        })
      );
    }

    // Create Document
    const doc = new Document({
      creator: "Financial Analysis System",
      title: "Financial Profile",
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(0.75),
                bottom: convertInchesToTwip(0.75),
                left: convertInchesToTwip(0.75),
                right: convertInchesToTwip(0.75),
              },
              size: {
                width: convertInchesToTwip(8.5),
                height: convertInchesToTwip(11),
                orientation: PageOrientation.PORTRAIT,
              },
            },
          },
          footers: {
            default: new Footer({
              children: footerChildren,
            }),
          },
          children: children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `Financial_Profile_${new Date().getTime()}.docx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating Word document:", error);
    alert("Failed to generate Word document. Please try again.");
  } finally {
    setDownloadingWord(false);
  }
};


  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (error)
    return (
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Data
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  const financialData = data?.content;

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mx-auto">
      {/* Header with Download Buttons */}
      <div className="mb-6 border-b border-gray-300 pb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl text-gray-700 font-light">
            Financial Profile
          </h2>

          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" /> PDF
                </>
              )}
            </button>

            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  Word
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Balance Sheet */}
      {financialData?.balance_sheet && (
        <div className="mb-6">
          <h3 className="text-md font-light text-gray-500 mb-3 flex items-center gap-2">
            Balance Sheet ({financialData.balance_sheet.year})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Total Assets
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.balance_sheet.total_assets?.value}{" "}
                {financialData.balance_sheet.total_assets?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Total Equity
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.balance_sheet.total_equity?.value}{" "}
                {financialData.balance_sheet.total_equity?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Total Liabilities
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.balance_sheet.total_liabilities?.value}{" "}
                {financialData.balance_sheet.total_liabilities?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Cash & Equivalents
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.balance_sheet.cash_and_equivalents?.value}{" "}
                {financialData.balance_sheet.cash_and_equivalents?.unit}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Income Statement */}
      {financialData?.income_statement && (
        <div className="mb-6">
          <h3 className="text-md font-light text-gray-500 mb-3 flex items-center gap-2">
            Income Statement ({financialData.income_statement.year})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">Revenue</p>
              <p className="text-md font-light text-gray-700">
                {financialData.income_statement.revenue?.value}{" "}
                {financialData.income_statement.revenue?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Gross Profit
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.income_statement.gross_profit?.value}{" "}
                {financialData.income_statement.gross_profit?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Operating Income
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.income_statement.operating_income?.value}{" "}
                {financialData.income_statement.operating_income?.unit}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Net Profit
              </p>
              <p className="text-md font-light text-gray-700">
                {financialData.income_statement.net_profit?.value}{" "}
                {financialData.income_statement.net_profit?.unit}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Ratios */}
      {financialData?.key_ratios && (
        <div className="mb-6 pt-4 border-t border-gray-300">
          <h3 className="text-md font-light text-gray-500 mb-3">
            Key Financial Ratios
          </h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-300 bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ratio
                  </th>
                  {financialData.key_ratios[0]?.values?.map((val: any) => (
                    <th
                      key={val.year}
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                    >
                      {val.year}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Unit
                  </th>
                </tr>
              </thead>
              <tbody>
                {financialData.key_ratios.map((ratio: any) => (
                  <tr
                    key={ratio.ratio}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {ratio.ratio}
                    </td>
                    {ratio.values.map((val: any) => (
                      <td
                        key={val.year}
                        className="px-4 py-3 text-sm text-gray-800 text-right"
                      >
                        {val.value}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {ratio.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Credit Metrics */}
      {financialData?.credit_metrics && (
        <div className="mb-6 pb-6 border-b border-gray-300">
          <h3 className="text-md font-light text-gray-500 mb-3">
            Credit Metrics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {Object.entries(financialData.credit_metrics).map(
              ([key, value]: [string, any]) => (
                <div key={key}>
                  <p className="text-gray-500 font-medium mb-1 text-sm capitalize">
                    {key.replace(/_/g, " ")}
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {value?.value} {value?.unit}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Citations */}
      {data?.citations && data.citations.length > 0 && (
        <div className="ml-1 text-gray-700 max-w-4xl text-sm mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources</h4>
          <p className="mb-1">
            {data.citations.map((sourceUrl: string, i: number) => {
              const url = new URL(sourceUrl);
              const display = url.hostname.replace("www.", "");
              return (
                <button
                  key={i}
                  onClick={(e) => handleSourceClick(e, sourceUrl)}
                  className="text-xs text-gray-700 border ml-1 mb-1 border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                >
                  {display}
                </button>
              );
            })}
          </p>
        </div>
      )}
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
